import * as React from 'react';
import { Trans } from '@lingui/react/macro';

export interface IConfiguratorValidationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onGoToConfig: () => void;
  configuratorType: 'DCC' | 'SCC';
}

/**
 * Dialog that appears when user tries to enable DCC/SCC feature but no links are configured.
 * Guides user to the configuration page to add the missing links.
 */
export default function ConfiguratorValidationDialog({
  isOpen,
  onClose,
  onGoToConfig,
  configuratorType,
}: IConfiguratorValidationDialogProps) {
  if (!isOpen) return null;

  const handleGoToConfig = () => {
    onGoToConfig(); // Navigate to config page
    onClose(); // Close dialog
  };

  const handleCancel = () => {
    onClose(); // Just close dialog without navigation
  };

  return (
    <div className="relative z-[101]" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-gray-10050 transition-opacity" aria-hidden="true"></div>
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
            <div>
              <div className="mt-3 text-center sm:mt-5">
                <h3 className="text-base font-semibold text-gray-900" id="modal-title">
                  <Trans>No {configuratorType} Link Configured</Trans>
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-700">
                    <Trans>
                      The {configuratorType} feature is enabled but no {configuratorType} links have been configured
                      yet. Please add {configuratorType} links in the configuration page.
                    </Trans>
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="hover:bg-gray-50 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal sm:w-auto"
                >
                  <Trans>Cancel</Trans>
                </button>
                <button
                  type="button"
                  onClick={handleGoToConfig}
                  className="inline-flex w-full justify-center rounded-md bg-teal px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal sm:w-auto"
                >
                  <Trans>Go to Config Page</Trans>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
